import React, { JSX } from "react";
import { useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import classNames from "classnames";

import "./CustomTable.scss";

export interface CustomTableColumn {
  Header: string;
  accessor: string;
}

export interface Column {
  Header: string;
  accessor: string;
  width?: number;
  minWidth?: number;
  sticky?: "left" | "right";
}

interface CustomTableProps {
  columns: Column[];
  data: any;
  rowClassName?: string;
  getRowClassName?: (row: any) => string | undefined;
  getRowProps?: (row: any) => Record<string, unknown>;
  headerClassName?: string;
  isLoading?: boolean;
  noRowsPlaceholder?: JSX.Element;
}

export function CustomTable({
  columns,
  data,
  rowClassName,
  getRowClassName,
  getRowProps,
  headerClassName,
  isLoading,
  noRowsPlaceholder,
}: CustomTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      // eslint-disable-next-line react-compiler/react-compiler
      useSticky,
    );

  return (
    <table {...getTableProps()} className="ws-custom-table">
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              const headerProps = column.getHeaderProps();
              return (
                <th
                  {...headerProps}
                  style={{
                    ...headerProps.style,
                    width: column.width,
                    // @ts-ignore -- minWidth isn't part of react-table's Column type
                    minWidth: column.minWidth,
                  }}
                  className={headerClassName}
                >
                  {column.render("Header")}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      {!isLoading && rows.length === 0 && noRowsPlaceholder ? (
        <tbody>
          <tr>
            <td colSpan={columns.length}>{noRowsPlaceholder}</td>
          </tr>
        </tbody>
      ) : (
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                {...getRowProps?.(row.original)}
                // @ts-ignore
                onClick={row.original?.onRowClick}
                className={classNames(
                  "ws-custom-table-row",
                  rowClassName,
                  getRowClassName?.(row.original),
                  // @ts-ignore
                  row.original?.onRowClick && "ws-custom-table-row--clickable",
                )}
                data-qa={row?.original?.name}
              >
                {row.cells.map((cell) => {
                  const cellProps = cell.getCellProps();
                  return (
                    <td
                      {...cellProps}
                      style={{
                        ...cellProps.style,
                        width: cell.column.width,
                        // @ts-ignore -- minWidth isn't part of react-table's Column type
                        minWidth: (cell.column as unknown as Column).minWidth,
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      )}
    </table>
  );
}
